// hydratorRegistry — the dependency inversion that kills the island violation (contract 07 §3,
// ledger A11). Legacy `service/StoreHydrator.ts:3-8` imported SIX feature stores directly; that
// import list *is* the violation the contract exists to kill.
//
// Here the direction flips: a feature REGISTERS a writer for the bootstrap section it owns, and 07
// dispatches `MappedBootstrapResult` to the registered writers without importing a single target
// store. 07 knows the section names (its own wire contract) — nothing about the stores behind them.
//
// The temporary "shim hydrator" (contract §17 step 2 / rollback) is simply a set of registrations
// against the legacy stores for still-unmigrated sections (reports/columns/dashboards/integrations/
// customize): register at boot, and drop them per-feature by calling the returned unregister as each
// feature migrates and registers its own writer — all without touching 07.
import type { BootstrapSection, MappedBootstrapResult } from '../models/bootstrap.types'

/** A section writer. Receives the full mapped result and picks the slice it owns (the legacy
 *  per-section hydrators each read several `MappedBootstrapResult` fields). */
export type BootstrapHydrator = (result: MappedBootstrapResult) => void

/** Optional teardown a feature registers so a context switch can clear its stale scope before the
 *  re-hydrate (contract §7: account switch resets workspace scope, workspace switch resets it too). */
export type BootstrapReset = () => void

export interface BootstrapHydratorDescriptor {
  hydrate: BootstrapHydrator
  reset?: BootstrapReset
}

const registry = new Map<BootstrapSection, BootstrapHydratorDescriptor>()

/**
 * Register the writer for a bootstrap section. Re-registering the same section REPLACES the prior
 * writer (last writer wins) — this is how a migrating feature takes over its section from the shim.
 * Returns an unregister fn that removes the writer only if it is still the active one (idempotent).
 */
export function registerBootstrapHydrator(
  section: BootstrapSection,
  hydrator: BootstrapHydrator | BootstrapHydratorDescriptor,
): () => void {
  const descriptor: BootstrapHydratorDescriptor =
    typeof hydrator === 'function' ? { hydrate: hydrator } : hydrator
  registry.set(section, descriptor)
  return () => {
    if (registry.get(section) === descriptor) registry.delete(section)
  }
}

/**
 * Dispatch a mapped bootstrap result to registered writers. With no `sections` every registered
 * writer fires (full boot); pass a subset to re-hydrate only those sections (e.g. a workspace switch
 * only re-runs workspace-scoped writers). Unregistered sections are silently skipped — a section
 * with no writer is a boot-wiring gap the flow surfaces, not this registry's concern.
 */
export function dispatchBootstrap(
  result: MappedBootstrapResult,
  sections?: readonly BootstrapSection[],
): void {
  const targets = sections ?? [...registry.keys()]
  for (const section of targets) {
    registry.get(section)?.hydrate(result)
  }
}

/**
 * Run the registered `reset` for the given sections (contract §7 switch reset). With no argument
 * every registered reset runs. Writers without a reset are skipped.
 */
export function resetBootstrapSections(sections?: readonly BootstrapSection[]): void {
  const targets = sections ?? [...registry.keys()]
  for (const section of targets) {
    registry.get(section)?.reset?.()
  }
}

/** Sections that currently have a registered writer — introspection for tests and boot-gap checks. */
export function getRegisteredSections(): BootstrapSection[] {
  return [...registry.keys()]
}

/** Drop every registration. Test isolation and the full rollback lever; not used on the hot path. */
export function clearBootstrapHydrators(): void {
  registry.clear()
}
